import { useEffect, useState } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Checkbox,
  Badge,
  Button,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import {
  DEFAULT_PROMO_DESIGN,
  DEFAULT_PROMO_TITLE,
  type PromoDesign,
  type PromoPreviewGift,
} from '../pages/widgets/promoDefaults';
import { PromoPreview } from '../pages/widgets/PromoPreview';
import { ProductBlockStatusBanner } from './ProductBlockStatusBanner';
import { t } from '../i18n';

export function CampaignPromoWidgetCard({
  enabled,
  onEnabledChange,
  title,
  onTitleChange,
  subtitle,
  onSubtitleChange,
  triggerValue,
  gifts,
  campaignReturn,
}: {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  title: string;
  onTitleChange: (v: string) => void;
  subtitle: string;
  onSubtitleChange: (v: string) => void;
  triggerValue: string;
  gifts: PromoPreviewGift[];
  campaignReturn: string;
}) {
  const navigate = useNavigate();
  const api = useApi();
  const [design, setDesign] = useState<PromoDesign>({ ...DEFAULT_PROMO_DESIGN });

  useEffect(() => {
    api
      .get<
        Array<{ type: string; styles?: Record<string, unknown> }>
      >('/widgets')
      .then((list) => {
        const w = (list || []).find((x) => x.type === 'PRODUCT_WIDGET');
        if (!w?.styles) return;
        const s = w.styles;
        setDesign({
          ...DEFAULT_PROMO_DESIGN,
          titleFontWeight:
            s.titleFontWeight === 'regular' ||
            s.titleFontWeight === 'medium' ||
            s.titleFontWeight === 'semibold' ||
            s.titleFontWeight === 'bold'
              ? s.titleFontWeight
              : DEFAULT_PROMO_DESIGN.titleFontWeight,
          subtitleFontWeight:
            s.subtitleFontWeight === 'regular' ||
            s.subtitleFontWeight === 'medium' ||
            s.subtitleFontWeight === 'semibold' ||
            s.subtitleFontWeight === 'bold'
              ? s.subtitleFontWeight
              : DEFAULT_PROMO_DESIGN.subtitleFontWeight,
          accentColor: String(s.accentColor ?? DEFAULT_PROMO_DESIGN.accentColor),
          backgroundColor: String(
            s.backgroundColor ?? DEFAULT_PROMO_DESIGN.backgroundColor,
          ),
          textColor: String(s.textColor ?? DEFAULT_PROMO_DESIGN.textColor),
          mutedColor: String(s.mutedColor ?? DEFAULT_PROMO_DESIGN.mutedColor),
          borderColor: String(s.borderColor ?? DEFAULT_PROMO_DESIGN.borderColor),
          giftCardBackground: String(
            s.giftCardBackground ?? DEFAULT_PROMO_DESIGN.giftCardBackground,
          ),
          giftCardBorder: String(
            s.giftCardBorder ?? DEFAULT_PROMO_DESIGN.giftCardBorder,
          ),
          comparePriceColor: String(
            s.comparePriceColor ?? DEFAULT_PROMO_DESIGN.comparePriceColor,
          ),
          borderRadius: Number(
            s.borderRadius ?? DEFAULT_PROMO_DESIGN.borderRadius,
          ),
          fontFamily: String(s.fontFamily ?? DEFAULT_PROMO_DESIGN.fontFamily),
          autoFitTheme: s.autoFitTheme !== false,
          themeSchemeId:
            typeof s.themeSchemeId === 'string' ? s.themeSchemeId : null,
        });
      })
      .catch(() => undefined);
  }, [api]);

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" wrap>
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {t("Product page promotion widget")}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {t(
                'Shows this offer on qualifying product pages: the title, subtitle, and gifts shoppers can unlock, with the offered price. Add the product block under Buy. If two campaigns enable this on the same product, only the highest-priority one appears.',
              )}
            </Text>
          </BlockStack>
          <Badge>{t("Product page")}</Badge>
        </InlineStack>
        <ProductBlockStatusBanner
          block="product-promo"
          blockName="Freebi Product promotion"
        />
        <Checkbox
          label={t("Show promotion widget on the product page")}
          checked={enabled}
          onChange={onEnabledChange}
          helpText={t("Requires the Product promotion theme block. Fonts and colors are under Widgets.")}
        />
        {enabled && (
          <>
            <TextField
              label={t("Title")}
              value={title}
              onChange={onTitleChange}
              autoComplete="off"
              maxLength={80}
              showCharacterCount
              placeholder={DEFAULT_PROMO_TITLE}
            />
            <TextField
              label={t("Subtitle")}
              value={subtitle}
              onChange={onSubtitleChange}
              autoComplete="off"
              maxLength={80}
              showCharacterCount
              multiline={2}
              helpText={t("Use {{trigger_value}} for the spend amount or item count.")}
            />
            <Button
              onClick={() =>
                navigate(
                  `/widgets/promo?return=${encodeURIComponent(campaignReturn)}`,
                )
              }
            >
              {t("Edit style in Widgets")}
            </Button>
            <Text as="p" variant="headingSm">
              {t("Preview")}
            </Text>
            <PromoPreview
              design={design}
              title={title || DEFAULT_PROMO_TITLE}
              subtitle={subtitle}
              triggerValue={triggerValue}
              gifts={gifts}
              mode="desktop"
            />
          </>
        )}
      </BlockStack>
    </Card>
  );
}
